import React, { Component } from 'react';

import { VALUES_PER_PAGE } from 'components/pagination/constants';
import { ORDER_BY } from 'components/table/constants';
import { Tabs } from 'components/tabs';
import { translate } from 'components/translate';
import { PAGE_VIEW_EVENTS } from 'configs/routes/constants';
import isEmpty from 'lodash/isEmpty';
import { inject, observer } from 'mobx-react';
import { CommunityService, CommunityStore } from 'modules/community';
import CommentList from 'modules/community/commentList/CommentList';
import Configuration from 'modules/community/configuration/Configuration';
import {
  COLUMNS_LIST,
  NOTIFICATION_STATUS,
  TABS_INDEXES
} from 'modules/community/constants';
import { ContentService } from 'modules/content';
import ContentBasePage from 'modules/content/components/ContentBasePage';
import { UserManagerService } from 'modules/userManager';
import { COLUMNS_LIST as USER_COLUMNS_LIST } from 'modules/userManager/constants';
import PropTypes from 'prop-types';

import { TABS } from './helpers';

import './CommunityManager.scss';

const commentColumnSort = COLUMNS_LIST.USER_NAME.sortName;

@inject((stores) => ({
  activedTab: stores.CommunityStore.activedTab,
  sortOptions: stores.CommunityStore.sortOptions,
  size: stores.CommunityStore.size
}))
@observer
@translate()
export default class CommunityManager extends Component {
  static propTypes = {
    activedTab: PropTypes.number,
    size: PropTypes.number,
    sortOptions: PropTypes.object,
    t: PropTypes.func,
    isMembership: PropTypes.bool
  };

  state = {
    loading: false,
    dataTableParams: {}
  };

  constructor(props) {
    super(props);

    CommunityService.clear();

    this.fetchCommentsData = this.fetchCommentsData.bind(this);
    this.onChangeSort = this.onChangeSort.bind(this);
    this.fetchUsers = this.fetchUsers.bind(this);
    this.updateTab = this.updateTab.bind(this);
  }

  async componentDidMount() {
    CommunityService.getCommunityNotifications();
    await CommunityService.getConfigurationData();

    async function fetchModulesPages(isExtraModule = false) {
      const modulesList = await ContentService.fetchModulesList(
        null,
        isExtraModule
      );

      const modulePagesListPromises = (modulesList || []).map((moduleItem) =>
        ContentService.fetchModulePagesList(moduleItem.id, isExtraModule)
      );

      await Promise.all(modulePagesListPromises);
    }

    await ContentService.init();

    fetchModulesPages();
    fetchModulesPages(true);
  }

  async fetchCommentsData({
    userAreaId,
    classId,
    dataTableParams = {},
    status = NOTIFICATION_STATUS.nonRevised,
    moduleId,
    pageId
  }) {
    const { sortOptions, size } = this.props;

    this.setState({ loading: true });

    const sort = sortOptions[commentColumnSort];
    const params = {
      userAreaId,
      page: dataTableParams.pagination ? dataTableParams.pagination.page : 0,
      size: dataTableParams.perPage || size,
      sort: `commentDate,${sort}`,
      status: this.getStatus(status),
      moduleIds: moduleId,
      pageIds: pageId,
      ...(classId && { classId })
    };

    await CommunityService.getCommentsList(params);

    CommunityService.checkCommentsBySelectAll();
    CommunityService.translateDefaultClassName();
    CommunityService.getCommunityNotifications();

    this.setState({
      loading: false,
      dataTableParams
    });
  }

  async fetchUsers(query) {
    try {
      CommunityService.setData({ loadingUsers: true, user: query });

      let usersData = {};

      if (isEmpty(query)) {
        CommunityService.setData({ usersData });

        return;
      }

      const filterType = 'simple';
      const parameters = {
        filters: [
          {
            _type: filterType,
            query
          }
        ],
        page: 0,
        size: VALUES_PER_PAGE.TEN,
        sort: `${USER_COLUMNS_LIST.USER_NAME.sortName},${ORDER_BY.ASC}`
      };

      const response = await UserManagerService.search(parameters);

      usersData = {
        content: response.content,
        numberOfElements: response.numberOfElements,
        totalElements: response.totalElements
      };

      CommunityService.setData({ usersData });
    } finally {
      CommunityService.setData({ loadingUsers: false });
    }
  }

  getStatus = (status) => {
    const statuses = {
      [NOTIFICATION_STATUS.all]: [
        NOTIFICATION_STATUS.approved,
        NOTIFICATION_STATUS.editing,
        NOTIFICATION_STATUS.markedAsSpamByUser,
        NOTIFICATION_STATUS.markedAsSpamByOwner,
        NOTIFICATION_STATUS.nonRevised,
        NOTIFICATION_STATUS.reproved
      ],
      [NOTIFICATION_STATUS.nonRevised]: [
        NOTIFICATION_STATUS.nonRevised,
        NOTIFICATION_STATUS.markedAsSpamByUser,
        NOTIFICATION_STATUS.editing
      ]
    };

    return statuses[status] || [status];
  };

  onChangeSort(sortOption) {
    const { sortOptions } = this.props;
    const { sortData } = sortOptions;
    const newSortOptions = { sortData, ...sortOption };

    CommunityStore.setSortOptions(newSortOptions);
    return newSortOptions;
  }

  updateTab(activedTab) {
    CommunityService.setData({ activedTab });
  }

  beforeChange = async (activedTab) => {
    this.updateTab(activedTab);

    CommunityService.getCommunityNotifications();

    if (activedTab !== TABS_INDEXES.configurations) {
      const resetedParams = CommunityService.resetCommentListParams(activedTab);

      await this.fetchCommentsData(resetedParams);
    }
  };

  get tabs() {
    const { t } = this.props;

    return TABS(t);
  }

  filterStatus = (status) =>
    ![
      NOTIFICATION_STATUS.markedAsSpamByUser,
      NOTIFICATION_STATUS.editing
    ].includes(NOTIFICATION_STATUS[status]);

  get commentStatusList() {
    return Object.keys(NOTIFICATION_STATUS)
      .filter(this.filterStatus)
      .map((status, index) => ({
        value: NOTIFICATION_STATUS[status],
        label: this.tabs[index].name
      }));
  }

  render() {
    const { loading, dataTableParams } = this.state;
    const { activedTab } = this.props;

    const isConfigTab = activedTab === TABS_INDEXES.configurations;

    return (
      <div className="community-manager-wrapper">
        <ContentBasePage className="content__grid">
          <ContentBasePage.Header title="general.comments" hideBackHandler />

          <Tabs
            track={PAGE_VIEW_EVENTS.COMMUNITY.MAIN}
            tabs={this.tabs}
            actived={activedTab}
            beforeTabChange={this.beforeChange}
            disable={loading}
          >
            {isConfigTab ? (
              <Configuration />
            ) : (
              <CommentList
                fetchCommentsData={this.fetchCommentsData}
                fetchUsers={this.fetchUsers}
                onChangeSort={this.onChangeSort}
                dataTableParams={dataTableParams}
                commentStatusList={this.commentStatusList}
                updateTab={this.updateTab}
                loading={loading}
              />
            )}
          </Tabs>
        </ContentBasePage>
      </div>
    );
  }
}
